import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  ProcessFlowchartValidationError,
  ProcessFlowchartObjectType,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {FLOWCHART_SIDEPANEL_Z_INDEX} from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

export interface FlowchartLockedBannerProps {
  locked: boolean;
  lockErrorMessage?: string;
}

function FlowchartLockedBanner({locked, lockErrorMessage}: FlowchartLockedBannerProps): JSX.Element | null {
  if (!locked || lockErrorMessage == null) {
    return null;
  }

  return <InlineBanner bannerType={InlineBannerType.WARNING} text={lockErrorMessage} />;
}

export interface FlowchartValidationErrorBannerProps {
  validationErrors: Array<ProcessFlowchartValidationError>;
}

function FlowchartValidationErrorBanner({
  validationErrors,
}: FlowchartValidationErrorBannerProps): JSX.Element | null {
  const flowchartLevelErrors = validationErrors.filter(
    (error) => error.object?.type === ProcessFlowchartObjectType.FLOWCHART_CONFIG || error.object == null
  );

  if (flowchartLevelErrors.length === 0) {
    return null;
  }

  return (
    <InlineBanner
      bannerType={InlineBannerType.ERROR}
      text={flowchartLevelErrors.map((error) => error.errorMessage).join('\n')}
    />
  );
}

export interface ProcessFlowchartBannersProps {
  locked: boolean;
  lockErrorMessage?: string;
  validationErrors: Array<ProcessFlowchartValidationError>;
}

export function ProcessFlowchartBanners({
  locked,
  lockErrorMessage,
  validationErrors,
}: ProcessFlowchartBannersProps): JSX.Element | null {
  const showLockedBanner = locked && lockErrorMessage != null;
  const flowchartLevelErrors = validationErrors.filter(
    (error) => error.object?.type === ProcessFlowchartObjectType.FLOWCHART_CONFIG || error.object == null
  );
  const showValidationBanner = flowchartLevelErrors.length > 0;

  if (!showLockedBanner && !showValidationBanner) {
    return null;
  }

  return (
    <div className={css(styles.bannersContainer)}>
      <FlowchartLockedBanner locked={locked} lockErrorMessage={lockErrorMessage} />
      <FlowchartValidationErrorBanner validationErrors={validationErrors} />
    </div>
  );
}

const styles = StyleSheet.create({
  bannersContainer: {
    position: 'absolute',
    top: '16px',
    left: '16px',
    zIndex: FLOWCHART_SIDEPANEL_Z_INDEX - 1,
    maxWidth: '800px',
    minWidth: '500px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
});
